import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";

const TEAM = [
  { role: "President", names: ["Komal Tomar"], tint: "maroon" },
  { role: "Secretary", names: ["Harsh Natekar", "Sanjana Unniyal"], tint: "gold" },
  { role: "Treasurer", names: ["Meet Agarwal", "Ammar Dalwai"], tint: "ember" },
  { role: "Advisor", names: ["T.S Anand"], tint: "ink" },
  { role: "Mentor", names: ["Kripal Shah"], tint: "maroon" },
  { role: "Public Relations Officer", names: ["Rahul Modi", "Shruti Shah"], tint: "ember" },
  { role: "Global Service Team", names: ["Vaidehi Manjre", "Vinay Kumhar"], tint: "ember" },
  { role: "Leadership Director", names: ["Nitika Singh"], tint: "gold" },
  { role: "Membership Director", names: ["Sangita Pramanik"], tint: "maroon" },
  { role: "Fellowship Director", names: ["Pankaj Agarwal"], tint: "ember" },
  { role: "Tamer & Tail Twister", names: ["Krish Mehta"], tint: "gold" },
];

const MEMBER_IMAGES = {
  "Komal Tomar": "/images/leadership/komal-tomar.jpg",
  "Harsh Natekar": "/images/leadership/harsh-natekar.jpg",
  "Meet Agarwal": "/images/leadership/meet-agarwal.jpg",
  "Ammar Dalwai": "/images/leadership/ammar-dalwai.jpg",
  "Vinay Kumhar": "/images/leadership/vinay-kumhar.jpg",
  "Sanjana Unniyal": "/images/leadership/sanjana-unniyal.jpg",
  "Vaidehi Manjre": "/images/leadership/vaidehi-manjre.jpg",
  "Nitika Singh": "/images/leadership/nitika-singh.jpg",
  "Sangita Pramanik": "/images/leadership/sangita-pramanik.jpg",
  "Pankaj Agarwal": "/images/leadership/pankaj-agarwal.jpg",
  "T.S Anand": "/images/leadership/ts-anand.jpg",
  "Kripal Shah": "/images/leadership/kripal-shah.jpg",
  "Krish Mehta": "/images/leadership/krish-mehta.jpg",
  "Rahul Modi": "/images/leadership/rahul-modi.jpg",
  "Shruti Shah": "/images/leadership/shruti-shah.jpg",
};

const TINT_COLORS = {
  maroon: { bg: "#8B1E2D", text: "#F4C430" },
  gold: { bg: "#F4C430", text: "#8B1E2D" },
  ember: { bg: "#E76F2E", text: "#FFFFFF" },
  ink: { bg: "#1A1A1A", text: "#F4C430" },
};

const monogram = (name) =>
  name
    .split(/\s+/)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

export default function Leadership() {
  return (
    <div className="pt-32 md:pt-40">
      {/* HEADER */}
      <section className="pb-16 md:pb-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <Reveal>
            <div className="font-inter text-[11px] tracking-[0.3em] uppercase text-[#E76F2E] mb-6">
              <span className="w-8 h-px bg-[#E76F2E] inline-block align-middle mr-3" /> Leadership
            </div>
            <h1 className="font-playfair text-5xl md:text-7xl lg:text-[7.5rem] font-semibold text-[#2C2C2C] leading-[0.95] tracking-tight max-w-5xl">
              Meet the <em className="text-[#8B1E2D]">team</em>.
            </h1>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-12 gap-8">
              <p className="md:col-span-7 font-inter text-[#555555] text-lg md:text-xl leading-relaxed">
                The people behind the work — leading, supporting, and building the Leo Club of Mumbai Carter Road together.
              </p>
              <p className="md:col-span-5 font-inter text-[#555555] text-base leading-relaxed md:border-l md:border-black/15 md:pl-6">
                Every role here carries responsibility, initiative, and the shared goal of creating meaningful impact.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TEAM GRID */}
      <section className="pb-24 md:pb-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <Stagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" stagger={0.08}>
            {TEAM.map((m, idx) => {
              const c = TINT_COLORS[m.tint] || TINT_COLORS.maroon;
              return (
                <StaggerItem key={m.role}>
                  <article
                    className="group bg-white border border-black/8 p-8 h-full flex flex-col transition-all duration-500 ease-[cubic-bezier(0.25,1,0.5,1)] hover:-translate-y-1.5 hover:shadow-[0_24px_50px_-20px_rgba(139,30,45,0.2)] hover:border-[#8B1E2D]/30"
                    data-testid={`leader-card-${m.role.toLowerCase().replace(/\s+/g, "-").replace(/[^\w-]/g, "")}`}
                  >
                    <div className="flex items-center gap-4 mb-6">
                      {m.names.map((n, i) => {
                        const img = MEMBER_IMAGES[n];
                        return (
                          <div
                            key={n}
                            className="relative rounded-full grain overflow-hidden flex items-center justify-center flex-shrink-0"
                            style={{
                              width: "120px",
                              height: "120px",
                              backgroundColor: c.bg,
                              marginLeft: i > 0 ? "-32px" : 0,
                              zIndex: m.names.length - i,
                              boxShadow: "0 0 0 3px #fff",
                            }}
                          >
                            {img ? (
                              <img
                                src={img}
                                alt={n}
                                loading="lazy"
                                className="w-full h-full object-cover"
                                style={["Nitika Singh", "Kripal Shah", "Rahul Modi", "Shruti Shah"].includes(n) ? { objectPosition: "center top" } : undefined}
                              />
                            ) : (
                              <span className="font-playfair text-xl font-semibold leading-none" style={{ color: c.text }}>
                                {monogram(n)}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    <h3 className="font-playfair text-xl md:text-2xl font-semibold text-[#2C2C2C] leading-tight">
                      {m.role}
                    </h3>
                    <div className="mt-4 pt-4 border-t border-black/10 font-inter text-[#555555] text-base leading-relaxed">
                      {m.names.map((n, i) => (
                        <span key={n}>
                          {n}
                          {i < m.names.length - 1 && <span className="text-[#8B1E2D] mx-2">&amp;</span>}
                        </span>
                      ))}
                    </div>
                  </article>
                </StaggerItem>
              );
            })}
          </Stagger>
        </div>
      </section>
    </div>
  );
}
